import React, { useState } from 'react';
import useStore from '../store/useStore';
import { useT } from '../i18n';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,24}$/;

// TLDs that are never real inboxes — reserved/test names or obvious joke endings.
const BANNED_TLDS = new Set([
  'test', 'example', 'invalid', 'localhost', 'local', 'internal',
  'fake', 'shit', 'lol', 'wtf', 'lmao', 'garbage', 'spam', 'xxx',
]);

// Common disposable / throwaway email providers.
const BANNED_DOMAINS = new Set([
  'mailinator.com', 'tempmail.com', 'temp-mail.org', '10minutemail.com',
  'guerrillamail.com', 'yopmail.com', 'trashmail.com', 'sharklasers.com',
  'getnada.com', 'dispostable.com', 'maildrop.cc', 'throwawaymail.com',
  'fakeinbox.com', 'mohmal.com', 'mintemail.com', 'mytemp.email',
  'emailondeck.com', 'tempinbox.com', 'inboxbear.com', 'spambox.us',
  'generated.com', 'example.com', 'test.com',
]);

// Popular email providers used to detect typos like gmail.co or gmial.com.
const POPULAR_PROVIDERS = [
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk',
  'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'proton.me', 'protonmail.com',
  'aol.com',
  'mail.ru', 'yandex.ru', 'yandex.com',
];

function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const prev = new Array(n + 1);
  const curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

// Returns the correct provider if the domain looks like a typo of a popular one,
// or null if it exactly matches a popular provider or is unrelated to any.
function findTypoSuggestion(domain) {
  if (POPULAR_PROVIDERS.includes(domain)) return null;
  let best = null;
  let bestDist = Infinity;
  for (const provider of POPULAR_PROVIDERS) {
    const d = levenshtein(domain, provider);
    if (d > 0 && d <= 2 && d < bestDist) {
      best = provider;
      bestDist = d;
    }
  }
  return best;
}

function validateEmail(raw) {
  const email = String(raw || '').trim().toLowerCase();
  if (!email) return { type: 'empty' };
  if (!EMAIL_REGEX.test(email)) return { type: 'format' };

  const domain = email.split('@')[1];
  if (BANNED_DOMAINS.has(domain)) return { type: 'disposable' };

  const tld = domain.split('.').pop();
  if (BANNED_TLDS.has(tld)) return { type: 'tld' };

  const suggestion = findTypoSuggestion(domain);
  if (suggestion) return { type: 'typo', suggestion };

  return null;
}

export default function AuthModal({ onSuccess, onCancel }) {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const setToken = useStore((s) => s.setToken);
  const t = useT();

  const finishAuth = async (data) => {
    if (data.success && data.data?.accessToken) {
      setToken(data.data.accessToken, data.data.refreshToken);
      await Promise.all([
        useStore.getState().loadUserConfig(),
        useStore.getState().loadUserMe(),
      ]);
      onSuccess();
      return true;
    }
    setError(data.message || 'Authentication failed');
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (mode === 'register') {
      const problem = validateEmail(email);
      if (problem?.type === 'format') { setError(t('auth.emailInvalid')); return; }
      if (problem?.type === 'disposable') { setError(t('auth.emailDisposable')); return; }
      if (problem?.type === 'tld') { setError(t('auth.emailTld')); return; }
      if (problem?.type === 'typo') {
        setError(`${t('auth.emailTypo')} ${problem.suggestion}?`);
        return;
      }
    }

    setLoading(true);

    const endpoint = mode === 'login' ? '/api/v1/auth/login' : '/api/v1/auth/register';
    const body = mode === 'login'
      ? { email, password }
      : { email, password, name };

    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      await finishAuth(data);
    } catch {
      setError(t('error.connection'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setError('');

    if (!GOOGLE_CLIENT_ID) {
      setError('Google Client ID is not configured');
      return;
    }
    if (!window.google?.accounts?.oauth2) {
      setError('Google Sign-In is still loading, please try again');
      return;
    }

    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: 'email profile',
      callback: async (response) => {
        if (!response?.access_token) {
          setError('Google authentication cancelled');
          setGoogleLoading(false);
          return;
        }
        try {
          const res = await fetch(`${API_BASE}/api/v1/auth/google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: response.access_token }),
          });
          const data = await res.json();
          await finishAuth(data);
        } catch {
          setError(t('error.connection'));
        } finally {
          setGoogleLoading(false);
        }
      },
      error_callback: () => {
        setError('Google authentication failed');
        setGoogleLoading(false);
      },
    });

    setGoogleLoading(true);
    client.requestAccessToken();
  };

  const busy = loading || googleLoading;

  return (
    <div className="auth-overlay" onClick={onCancel}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-header">
          <h3>{mode === 'login' ? t('auth.login') : t('auth.register')}</h3>
          <button className="auth-close" onClick={onCancel}>&times;</button>
        </div>

        <div className="auth-tabs">
          <button
            className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
            onClick={() => { setMode('login'); setError(''); }}
          >
            {t('auth.login')}
          </button>
          <button
            className={`auth-tab ${mode === 'register' ? 'active' : ''}`}
            onClick={() => { setMode('register'); setError(''); }}
          >
            {t('auth.register')}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <button
            type="button"
            className="auth-google-btn"
            onClick={handleGoogleLogin}
            disabled={busy}
          >
            <svg className="auth-google-icon" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
            </svg>
            <span>{googleLoading ? t('auth.wait') : t('auth.continueWithGoogle')}</span>
          </button>

          <div className="auth-divider"><span>{t('auth.or')}</span></div>

          {mode === 'register' && (
            <div className="auth-field">
              <label>{t('auth.name')}</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('auth.namePlaceholder')}
                required
              />
            </div>
          )}
          <div className="auth-field">
            <label>{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              required
            />
          </div>
          <div className="auth-field">
            <label>{t('auth.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordPlaceholder')}
              required
              minLength={6}
            />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button className="auth-submit" type="submit" disabled={busy}>
            {loading ? t('auth.wait') : mode === 'login' ? t('auth.login') : t('auth.register')}
          </button>
        </form>
      </div>
    </div>
  );
}
